import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Check, Upload, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useToast } from '../components/ui/use-toast';
import { useAuth } from '../context/AuthContext';
import AddSyndicatorModal from '../components/AddSyndicatorModal';
import ImportFundedDealsModal from '../components/ImportFundedDealsModal';
import DealStatusDropdown, { STATUSES } from '../components/DealStatusDropdown';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const FundedDeals = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showAddSyndicator, setShowAddSyndicator] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [autoClearEnabled, setAutoClearEnabled] = useState(false);
  const [paymentDay, setPaymentDay] = useState('Monday');
  const [syndicatorInfo, setSyndicatorInfo] = useState({});
  const [filterRep, setFilterRep] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const isSyndicator = user?.role === 'syndicator' || user?.role === 'rep';

  useEffect(() => {
    if (user) loadDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadDeals = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const storedUser = JSON.parse(localStorage.getItem('mcaUser') || '{}');
      const response = await axios.get(`${API_BASE}/deals?type=funded`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const dealsList = response.data || [];
      setDeals(dealsList);

      // If syndicator, load their participation info for each deal
      if ((storedUser?.role === 'syndicator' || storedUser?.role === 'rep') && dealsList.length > 0) {
        const synInfo = {};
        for (const deal of dealsList) {
          try {
            const synRes = await axios.get(`${API_BASE}/deals/${deal.id}/syndicators`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const mySyn = (synRes.data || []).find(s => 
              s.email?.toLowerCase() === storedUser.email?.toLowerCase()
            );
            if (mySyn) synInfo[deal.id] = mySyn;
          } catch (e) { /* skip */ }
        }
        setSyndicatorInfo(synInfo);
      }
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async (dealId) => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/deals/${dealId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(Array.isArray(response.data) ? response.data : []);
      
      // Load auto-clear settings
      const dealResponse = await axios.get(`${API_BASE}/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoClearEnabled(dealResponse.data.auto_clear_payments || false);
      setPaymentDay(dealResponse.data.payment_day || 'Monday');
    } catch (error) {
      console.error('Error loading payments:', error);
      setPayments([]);
    }
  };

  const clearPayment = async (dealId, paymentNumber) => {
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${dealId}/payments/${paymentNumber}/clear`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Payment marked as cleared' });
      loadPayments(dealId);
      loadDeals(); // Refresh totals
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to clear payment', variant: 'destructive' });
    }
  };

  const toggleAutoClear = async () => {
    if (!selectedDeal) return;
    try {
      const token = localStorage.getItem('mcaToken');
      const newState = !autoClearEnabled;
      await axios.put(`${API_BASE}/deals/${selectedDeal.id}`, { 
        auto_clear_payments: newState,
        payment_day: paymentDay 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoClearEnabled(newState);
      toast({ title: 'Success', description: `Auto-clear ${newState ? 'enabled' : 'disabled'}` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to toggle auto-clear', variant: 'destructive' });
    }
  };

  const updatePaymentDay = async (day) => {
    if (!selectedDeal) return;
    setPaymentDay(day);
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${selectedDeal.id}`, { payment_day: day }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: `Payment day set to ${day}` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update payment day', variant: 'destructive' });
    }
  };

  const updatePaymentStatus = async (paymentNumber, status) => {
    if (!selectedDeal) return;
    const statusMap = {
      'cleared': 'clear',
      'missed': 'miss',
      'bounced': 'bounce'
    };
    const endpoint = statusMap[status];
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${selectedDeal.id}/payments/${paymentNumber}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: `Payment marked as ${status}` });
      loadPayments(selectedDeal.id);
      loadDeals(); // Refresh totals
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update payment', variant: 'destructive' });
    }
  };

  const handleDeleteDeal = async (deal) => {
    if (!window.confirm(`Delete funded deal for ${deal.business}? This will remove all payment records and syndicators. This action cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/deals/${deal.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: `Deal for ${deal.business} deleted successfully` });
      loadDeals();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete deal', variant: 'destructive' });
    }
  };


  // Get unique reps/companies for the filter dropdown
  const uniqueReps = [...new Set(deals.map(d => d.inhouse || d.iso || '').filter(Boolean))];
  const uniqueStatuses = [...new Set(deals.map(d => d.paid_status || 'Current').filter(Boolean))];

  const filteredDeals = deals.filter(d => {
    // Text search
    const matchesSearch = !searchTerm || 
      d.business?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.id?.toString().includes(searchTerm);
    
    // Rep/Company filter
    const matchesRep = !filterRep || 
      (d.inhouse || d.iso || '') === filterRep;
    
    // Paid Status filter — normalize legacy "Current" to "Active"
    const dealStatus = (d.paid_status && d.paid_status !== 'Current') ? d.paid_status : 'Active';
    const matchesStatus = !filterStatus || dealStatus === filterStatus;
    
    // Date range filter
    let matchesDate = true;
    if (filterDateFrom || filterDateTo) {
      const dealDate = d.date || d.funding_date || '';
      if (dealDate) {
        if (filterDateFrom && dealDate < filterDateFrom) matchesDate = false;
        if (filterDateTo && dealDate > filterDateTo) matchesDate = false;
      }
    }
    
    return matchesSearch && matchesRep && matchesStatus && matchesDate;
  });

  // Calculate grand totals using blueprint definitions - based on FILTERED deals
  const totalsCalc = filteredDeals.reduce((acc, deal) => {
    const advanced = deal.advanced || deal.funding_amount || 0;
    const payback = deal.payback || deal.payback_amount || 0;
    const weekly = deal.payment_frequency === 'weekly' ? (deal.payment_amount || 0) : 0;
    const daily = deal.payment_frequency === 'daily' ? (deal.payment_amount || 0) : 0;
    // Prefer the explicitly-stored net_amount (net wired to merchant) — falls back for legacy rows
    const net = deal.net_amount != null
      ? deal.net_amount
      : (deal.net_funded != null ? deal.net_funded : (advanced - (deal.origination_fee_amount || 0)));
    const collected = deal.collected || 0;
    const remaining = payback - collected;
    const term = deal.term_length || 0;
    const factor = deal.factor_rate || 0;
    
    return {
      advanced: acc.advanced + advanced,
      payback: acc.payback + payback,
      weekly: acc.weekly + weekly,
      daily: acc.daily + daily,
      net: acc.net + net,
      collected: acc.collected + collected,
      remaining: acc.remaining + remaining,
      termSum: acc.termSum + term,
      factorSum: acc.factorSum + factor,
      count: acc.count + 1
    };
  }, {
    advanced: 0, payback: 0, weekly: 0, daily: 0, net: 0,
    collected: 0, remaining: 0, termSum: 0, factorSum: 0, count: 0
  });

  const grandTotals = {
    advanced: totalsCalc.advanced,
    payback: totalsCalc.payback,
    weekly: totalsCalc.weekly,
    daily: totalsCalc.daily,
    avgTermF: totalsCalc.count > 0 
      ? `${Math.round(totalsCalc.termSum / totalsCalc.count)}/${(totalsCalc.factorSum / totalsCalc.count).toFixed(2)}`
      : 'N/A',
    net: totalsCalc.net,
    collected: totalsCalc.collected,
    // Portfolio-level Paid Off % = TOTAL collected / TOTAL net wired (dollar-weighted).
    // Example: $619k net, $6,590.90 collected → 1.06%.
    avgPaidOff: totalsCalc.net > 0 ? (totalsCalc.collected / totalsCalc.net * 100) : 0,
    remaining: totalsCalc.remaining
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (selectedDeal && !showAddSyndicator) {
    return (
      <div className="p-8">
        <div className="mb-6">
          <Button variant="outline" onClick={() => setSelectedDeal(null)}>← Back to Funded Deals</Button>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">{selectedDeal.business} - Payment Schedule</h2>
          <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded">
            <div>
              <p className="text-sm text-gray-600">Funded Amount</p>
              <p className="text-xl font-bold">${selectedDeal.advanced?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Payback Amount</p>
              <p className="text-xl font-bold">${selectedDeal.payback?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Collected</p>
              <p className="text-xl font-bold text-green-600">${selectedDeal.collected?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Remaining</p>
              <p className="text-xl font-bold text-orange-600">${selectedDeal.remaining?.toLocaleString()}</p>
            </div>
          </div>

          <div className="overflow-x-auto" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Payment #</th>
                  <th className="text-left py-3 px-4">Due Date</th>
                  <th className="text-left py-3 px-4">Amount</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Cleared Date</th>
                  <th className="text-left py-3 px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment, idx) => {
                  const isPending = payment.status === 'pending';
                  const dueDate = new Date(payment.due_date);
                  const isOverdue = isPending && dueDate < new Date();
                  
                  return (
                    <tr key={idx} className={`border-b ${
                      payment.status === 'cleared' ? 'bg-green-50' :
                      isOverdue ? 'bg-red-50' : 'bg-white'
                    }`}>
                      <td className="py-2 px-4">{payment.payment_number}</td>
                      <td className="py-2 px-4">{dueDate.toLocaleDateString()}</td>
                      <td className="py-2 px-4">${payment.amount}</td>
                      <td className="py-2 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          payment.status === 'cleared' ? 'bg-green-100 text-green-700' :
                          isOverdue ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {payment.status === 'cleared' ? 'Cleared' : isOverdue ? 'Overdue' : 'Pending'}
                        </span>
                      </td>
                      <td className="py-2 px-4">{payment.cleared_date ? new Date(payment.cleared_date).toLocaleDateString() : '-'}</td>
                      <td className="py-2 px-4">
                        {isPending && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => clearPayment(selectedDeal.id, payment.payment_number)}
                          >
                            <Check size={14} className="mr-1" /> Clear
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 flex flex-col" style={{ height: 'calc(100vh - 100px)' }}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Funded Deals</h1>
        <div className="flex gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search deals"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Button 
            onClick={() => setShowImportModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="import-csv-button"
          >
            <Upload size={18} className="mr-2" />
            Import CSV
          </Button>
          <Button variant="outline" onClick={() => {
            const token = localStorage.getItem('mcaToken');
            const params = new URLSearchParams({ token });
            if (filterDateFrom) params.set('date_from', filterDateFrom);
            if (filterDateTo) params.set('date_to', filterDateTo);
            if (filterStatus) params.set('status', filterStatus);
            window.open(`${API_BASE}/reports/csv?${params.toString()}`, '_blank');
          }} data-testid="export-csv-btn">Export CSV</Button>
          <Button variant="outline" onClick={() => {
            const token = localStorage.getItem('mcaToken');
            const params = new URLSearchParams({ token });
            if (filterDateFrom) params.set('date_from', filterDateFrom);
            if (filterDateTo) params.set('date_to', filterDateTo);
            if (filterStatus) params.set('status', filterStatus);
            window.open(`${API_BASE}/reports/pdf?${params.toString()}`, '_blank');
          }} data-testid="export-pdf-btn">Export PDF</Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-4 mb-4 bg-white rounded-lg shadow px-4 py-3 flex-wrap" data-testid="filter-bar">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Filter:</span>
          <select
            value={filterRep}
            onChange={(e) => setFilterRep(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm bg-white min-w-[160px]"
            data-testid="filter-rep"
          >
            <option value="">All Reps</option>
            {uniqueReps.map((rep, i) => (
              <option key={i} value={rep}>{rep}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Paid Status:</span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm bg-white min-w-[140px]"
            data-testid="filter-status"
          >
            <option value="">All</option>
            {STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Funded Range:</span>
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
            data-testid="filter-date-from"
          />
          <span className="text-sm text-gray-400">to</span>
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
            data-testid="filter-date-to"
          />
        </div>

        {(filterRep || filterStatus || filterDateFrom || filterDateTo) && (
          <button
            onClick={() => { setFilterRep(''); setFilterStatus(''); setFilterDateFrom(''); setFilterDateTo(''); }}
            className="text-xs text-red-500 hover:text-red-700 underline"
            data-testid="filter-clear"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="flex-1 overflow-auto bg-white rounded-lg shadow">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="border-b border-gray-200">
              <th className="px-4 py-3 text-left font-semibold text-gray-700">#</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Business</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Company</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Advanced $</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Payback $</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Weekly $</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Daily $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Term/F</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Net $</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Collected $</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Paid Off %</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Remaining $</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">P&L</th>
              {isSyndicator && (
                <>
                  <th className="px-4 py-3 text-right font-semibold text-blue-700">My %</th>
                  <th className="px-4 py-3 text-right font-semibold text-blue-700">My Investment</th>
                  <th className="px-4 py-3 text-right font-semibold text-blue-700">My RTR</th>
                  <th className="px-4 py-3 text-right font-semibold text-blue-700">ISO CM</th>
                  <th className="px-4 py-3 text-right font-semibold text-green-700">Net Take Home</th>
                </>
              )}
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDeals.map((deal, idx) => {
              const mySyn = syndicatorInfo[deal.id];
              const pct = mySyn?.percentage || 0;
              const commPct = mySyn?.commission_percent || 0;
              const fullRtr = mySyn?.rtr || 0;
              // ISO CM is flat — set by admin, paid off the rip
              const isoCm = fullRtr * (commPct / 100);
              // My RTR moves live with the deal
              const collected = deal.collected || 0;
              const myCollected = collected * (pct / 100);
              // Net = live RTR minus the flat ISO CM already paid
              const myNetNow = myCollected - isoCm;
              return (
              <tr
                key={idx}
                className="border-b border-gray-100 hover:bg-orange-50 cursor-pointer transition"
                onClick={() => {
                  setSelectedDeal(deal);
                  loadPayments(deal.id);
                }}
              >
                <td className="px-4 py-3 text-gray-900">{deal.display_number ?? deal.id}</td>
                <td className="px-4 py-3 text-gray-900">{deal.date || 'N/A'}</td>
                <td 
                  className="px-4 py-3 text-blue-600 font-medium hover:underline cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/deals/${deal.display_number ?? deal.id}`);
                  }}
                >
                  {deal.business || deal.business_name || 'N/A'}
                </td>
                <td className="px-4 py-3 text-gray-600">{deal.inhouse || 'N/A'}</td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium">${(deal.advanced || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium">${(deal.payback || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {deal.payment_frequency === 'weekly' ? `$${(deal.payment_amount || 0).toLocaleString()}` : '-'}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {deal.payment_frequency === 'daily' ? `$${(deal.payment_amount || 0).toLocaleString()}` : '-'}
                </td>
                <td className="px-4 py-3 text-gray-600">{deal.term || 'N/A'}</td>
                <td className="px-4 py-3 text-right text-gray-900">
                  ${(deal.net_funded || (deal.advanced - (deal.origination_fee_amount || 0))).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right text-gray-900 font-semibold">${(deal.collected || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {(() => {
                    // Fallback if backend paid_off is stale: collected / net wired × 100
                    const netAmt = deal.net_amount != null ? deal.net_amount : (deal.net_funded != null ? deal.net_funded : (deal.advanced || 0));
                    const col = deal.collected || 0;
                    const computed = netAmt > 0 ? (col / netAmt * 100) : 0;
                    const val = deal.paid_off != null ? deal.paid_off : computed;
                    return `${val.toFixed(2)}%`;
                  })()}
                </td>
                <td className="px-4 py-3 text-right font-semibold" style={{
                  color: ((deal.payback || 0) - (deal.collected || 0)) < 0 ? '#dc2626' : '#ea580c'
                }}>
                  ${Math.abs((deal.payback || 0) - (deal.collected || 0)).toLocaleString()}
                  {((deal.payback || 0) - (deal.collected || 0)) < 0 && ' (overpaid)'}
                </td>
                {/* P&L Column — primary (collected − net wired) + expanded (net of all costs) inline */}
                {(() => {
                  const collected = deal.collected || 0;
                  const netAmt = deal.net_amount != null ? deal.net_amount : (deal.net_funded != null ? deal.net_funded : (deal.advanced || 0));
                  const pnl = deal.deal_pnl != null ? deal.deal_pnl : (collected - netAmt);
                  const pnlNet = deal.deal_pnl_net_of_costs != null
                    ? deal.deal_pnl_net_of_costs
                    : (pnl - (deal.commission_total || 0) - (deal.total_expenses || 0));
                  const funded = deal.advanced || 0;
                  const margin = funded > 0 ? ((pnl / funded) * 100).toFixed(1) : '0';
                  return (
                    <td className="px-4 py-3 text-right" data-testid={`pnl-cell-${deal.id}`}>
                      <div className={`font-semibold ${pnl >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                        ${pnl.toLocaleString(undefined, {minimumFractionDigits: 0})} <span className="text-xs font-normal">({margin}%)</span>
                      </div>
                      <div className={`text-[11px] ${pnlNet >= 0 ? 'text-green-600' : 'text-red-500'}`} title="Net of commissions, expenses & syndicator payouts">
                        Net of costs: ${pnlNet.toLocaleString(undefined, {minimumFractionDigits: 0})}
                      </div>
                    </td>
                  );
                })()}
                {isSyndicator && mySyn && (
                  <>
                    <td className="px-4 py-3 text-right text-blue-700 font-medium">{mySyn.percentage}%</td>
                    <td className="px-4 py-3 text-right text-blue-700">${(mySyn.participated_amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-blue-700">${myCollected.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    <td className="px-4 py-3 text-right text-red-600">${isoCm.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    <td className="px-4 py-3 text-right text-green-700 font-bold">${myNetNow.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                  </>
                )}
                {isSyndicator && !mySyn && (
                  <>
                    <td className="px-4 py-3 text-right text-gray-400">-</td>
                    <td className="px-4 py-3 text-right text-gray-400">-</td>
                    <td className="px-4 py-3 text-right text-gray-400">-</td>
                    <td className="px-4 py-3 text-right text-gray-400">-</td>
                    <td className="px-4 py-3 text-right text-gray-400">-</td>
                  </>
                )}
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()} data-testid={`funded-status-cell-${deal.id}`}>
                  <DealStatusDropdown
                    dealId={deal.id}
                    currentStatus={deal.paid_status}
                    onChanged={() => loadDeals()}
                    size="sm"
                  />
                </td>
                <td className="px-4 py-3">
                  <button
                    className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDeal(deal);
                    }}
                    title="Delete Deal"
                    data-testid={`delete-deal-${deal.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
          <tfoot className="sticky bottom-0 bg-gray-800 text-white border-t-2 border-orange-500 shadow-lg z-20">
            <tr>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-300 uppercase">Total advanced</span>
                  <span className="font-bold text-white">${grandTotals.advanced.toLocaleString()}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-300 uppercase">Total payback</span>
                  <span className="font-bold text-white">${grandTotals.payback.toLocaleString()}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-300 uppercase">Total weekly</span>
                  <span className="font-bold text-white">${grandTotals.weekly.toLocaleString()}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-300 uppercase">Total daily</span>
                  <span className="font-bold text-white">${grandTotals.daily.toLocaleString()}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-left">
                <div className="flex flex-col items-start">
                  <span className="text-xs text-gray-300 uppercase">AVG Term/F</span>
                  <span className="font-bold text-white">{grandTotals.avgTermF}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-300 uppercase">Total NET</span>
                  <span className="font-bold text-white">${grandTotals.net.toLocaleString()}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-300 uppercase">Total collected</span>
                  <span className="font-bold text-white">${grandTotals.collected.toLocaleString()}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-300 uppercase">Paid Off %</span>
                  <span className="font-bold text-white" data-testid="totals-paid-off">{grandTotals.avgPaidOff.toFixed(2)}%</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-300 uppercase">Total Remaining</span>
                  <span className="font-bold text-white">${grandTotals.remaining.toLocaleString()}</span>
                </div>
              </td>
              {/* Total P&L (primary + net-of-costs inline) */}
              <td className="px-4 py-3 text-right" data-testid="totals-pnl">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-gray-300 uppercase">Total P&L</span>
                  {(() => {
                    let totalPnl = 0;
                    let totalPnlNet = 0;
                    filteredDeals.forEach((d) => {
                      const collected = d.collected || 0;
                      const netAmt = d.net_amount != null ? d.net_amount : (d.net_funded != null ? d.net_funded : (d.advanced || 0));
                      const pnl = d.deal_pnl != null ? d.deal_pnl : (collected - netAmt);
                      const pnlNet = d.deal_pnl_net_of_costs != null
                        ? d.deal_pnl_net_of_costs
                        : (pnl - (d.commission_total || 0) - (d.total_expenses || 0));
                      totalPnl += pnl;
                      totalPnlNet += pnlNet;
                    });
                    return (
                      <>
                        <span className={`font-bold ${totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ${totalPnl.toLocaleString()}
                        </span>
                        <span className={`text-[10px] ${totalPnlNet >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          Net of costs: ${totalPnlNet.toLocaleString()}
                        </span>
                      </>
                    );
                  })()}
                </div>
              </td>
              {isSyndicator && (
                <>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-blue-300 uppercase">Avg %</span>
                      <span className="font-bold text-blue-300">
                        {filteredDeals.length > 0 ? (filteredDeals.reduce((sum, d) => sum + (syndicatorInfo[d.id]?.percentage || 0), 0) / filteredDeals.filter(d => syndicatorInfo[d.id]).length || 0).toFixed(0) : 0}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-blue-300 uppercase">Total Invested</span>
                      <span className="font-bold text-blue-300">
                        ${filteredDeals.reduce((sum, d) => sum + (syndicatorInfo[d.id]?.participated_amount || 0), 0).toLocaleString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-blue-300 uppercase">Total My RTR</span>
                      <span className="font-bold text-blue-300">
                        ${filteredDeals.reduce((sum, d) => {
                          const syn = syndicatorInfo[d.id];
                          if (!syn) return sum;
                          return sum + ((d.collected || 0) * (syn.percentage / 100));
                        }, 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-red-300 uppercase">Total ISO CM</span>
                      <span className="font-bold text-red-300">
                        ${filteredDeals.reduce((sum, d) => {
                          const syn = syndicatorInfo[d.id];
                          if (!syn) return sum;
                          return sum + ((syn.rtr || 0) * ((syn.commission_percent || 0) / 100));
                        }, 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-green-300 uppercase">Total Net</span>
                      <span className="font-bold text-green-300">
                        ${filteredDeals.reduce((sum, d) => {
                          const syn = syndicatorInfo[d.id];
                          if (!syn) return sum;
                          const myCol = (d.collected || 0) * (syn.percentage / 100);
                          const isoCm = (syn.rtr || 0) * ((syn.commission_percent || 0) / 100);
                          return sum + (myCol - isoCm);
                        }, 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </span>
                    </div>
                  </td>
                </>
              )}
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Add Syndicator Modal */}
      {showAddSyndicator && selectedDeal && (
        <AddSyndicatorModal
          dealId={selectedDeal.id}
          dealFunding={selectedDeal.advanced || selectedDeal.funding_amount || 0}
          dealPayback={selectedDeal.payback || selectedDeal.payback_amount || 0}
          onClose={() => {
            setShowAddSyndicator(false);
            setSelectedDeal(null);
          }}
          onSuccess={() => {
            toast({ title: 'Success', description: 'Syndicator added. Open deal detail to view.' });
            setShowAddSyndicator(false);
            setSelectedDeal(null);
          }}
        />
      )}

      {showImportModal && (
        <ImportFundedDealsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={loadDeals}
        />
      )}
    </div>
  );
};

export default FundedDeals;
